"use client";

import React from "react";
import { Carousel } from "@bfl-ui-toolkit/carousel";
import { currencify } from "@repo/shared-utils/num-utils";
import BFLLink from "@repo/shared-ui/BFLLink";
import BFLAccordion from "@repo/shared-ui/BFLAccordion";
import { ISimilarSchemesInfo, IOtherFunds } from "../../interfaces/AMCChartData.interface";
import { normalizeSchemeName } from "../../services/AMCService";
import { selectLoginStatus, useAppSelector } from "@repo/global-store";
import { CombinedClevertapProps, dataLayer } from "@repo/analytics/data-layer-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";

interface OtherFundsProps {
  content: IOtherFunds;
  similarSchemes: ISimilarSchemesInfo;
  companyName: string;
  version: string;
}

export const OtherFunds: React.FC<OtherFundsProps> = ({
  content,
  similarSchemes,
  companyName,
  version,
}) => {
  const {
    tagData = "2x in 3 years",
    minInvestLabel = "Minimum Investment",
    returnsLabel = "3Y Return",
    title = "",
  } = content;
  const heading = title?.replace("Bajaj", "");

  const loginStatus = useAppSelector(selectLoginStatus);

  const { similarSchemes: schemes } = similarSchemes;
  type SimilarScheme = ISimilarSchemesInfo["similarSchemes"][number];

  const handleCardClick = (fund: SimilarScheme, index: number) => {
    const payload = {
      component: "other_funds",
      element: "my_relations_status_progress_bar_carousel_click",
      event: "click",
      event_type: "carousel_click",
      destination_url: "NA",
      section_title: `${heading} ${companyName}`,
      sub_section: fund?.schName,
      cta_text: fund?.schName,
      nudge_type: "TBD",
      nudge_value: "TBD",
      product_name: "bajaj_mutual_fund",
      category_name: "loan",
      page_type: "pdp",
      component_position: index,
      component_id: fund?.schName,
    };

    dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  return (
    <div id="similarFunds">
      <BFLAccordion title={`${heading} ${companyName}`} isAccordion={false}>
        <Carousel slidesPerView={2.75} showDots={false} showNavigation className="w-full">
          {schemes?.map((fund, index) => {
            const {
              logo,
              altName,
              schName,
              mainCategory,
              category,
              schRating,
              minSIP,
              threeYrRet,
            } = fund;

            const showBadge = Number(threeYrRet ?? 0) > 100;
            const otherFundsLink = normalizeSchemeName(altName);

            return (
              <BFLLink
                key={schName}
                href={(isOpenInApp() ? `/${version}` : "") + `/investments/${otherFundsLink}`}
                dlProps={JSON.stringify({
                  component: "other_funds",
                  element: "my_relations_status_progress_bar_carousel_click",
                  event: "click",
                  event_type: "carousel_click",
                  destination_url: "NA",
                  section_title: `${heading} ${companyName}`,
                  sub_section: fund?.schName,
                  cta_text: fund?.schName,
                  ...(tagData && { nudge_value: tagData }),
                  ...(tagData && { nudge_type: tagData }),
                  product_name: "bajaj_mutual_fund",
                  category_name: "loan",
                  page_type: "pdp",
                  component_position: index,
                  component_id: fund?.schName,
                })}
                ctProps={JSON.stringify({
                  component: "other_funds",
                  element: "my_relations_status_progress_bar_carousel_click",
                  event: "click",
                  event_type: "carousel_click",
                  destination_url: "NA",
                  section_title: `${heading} ${companyName}`,
                  sub_section: fund?.schName,
                  cta_text: fund?.schName,
                  ...(tagData && { nudge_value: tagData }),
                  ...(tagData && { nudge_type: tagData }),
                  product_name: "bajaj_mutual_fund",
                  category_name: "loan",
                  page_type: "pdp",
                  component_position: index,
                  component_id: fund?.schName,
                })}
                className="relative mr-4 flex h-full min-h-[194px] flex-col rounded-md border border-gray-200 bg-white p-4 pt-6"
              >
                {showBadge && (
                  <span className="absolute top-0 left-0 bg-[linear-gradient(270deg,#FF9D77_0%,#FD6326_100%)] px-2 py-0 text-[10px] font-medium text-white">
                    {tagData || "2X in 3Years"}
                  </span>
                )}

                {/* Header */}
                <div className="mb-2 flex items-start justify-between">
                  <div className="flex items-center gap-[10px]">
                    <img src={logo} alt={altName} className="h-6 w-6 rounded-full md:h-9 md:w-9" />

                    <div>
                      <h2 className="mb-2 w-[60%] text-xs leading-5 font-medium md:text-sm">
                        {schName}
                      </h2>

                      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                        <span className="bg-dblue-100 rounded px-1 py-0.5 text-[8px] font-medium uppercase md:text-[10px]">
                          {mainCategory}
                        </span>

                        <span className="bg-dblue-100 rounded px-1 py-0.5 text-[8px] font-medium whitespace-nowrap uppercase md:text-[10px]">
                          {category}
                        </span>

                        <span className="text-dyellow-100 bg-lyellow-100 flex items-center gap-1 rounded px-1 py-0.5 text-[10px] whitespace-nowrap uppercase">
                          {schRating}
                          <i className="icomoon bg-lyellow-100 text-[8px] before:content-['\e930'] md:text-[11px]"></i>
                        </span>
                      </div>
                    </div>
                  </div>

                  <button onClick={() => handleCardClick(fund, index)}>
                    <i className="icomoon text-sm text-gray-900 before:content-['\e911']"></i>
                  </button>
                </div>

                {/* Bottom box */}
                <div className="bg-lgray-100 mt-auto flex items-center justify-between gap-5 rounded-md border border-gray-200 p-3 text-xs md:text-sm">
                  {minSIP !== undefined && minSIP !== null && (
                    <div>
                      <p className="text-gray-600">{minInvestLabel}</p>
                      <p className="font-medium">{currencify(minSIP)}</p>
                    </div>
                  )}

                  {threeYrRet !== undefined && threeYrRet !== null && (
                    <div className="text-right">
                      <p className="text-gray-600">{returnsLabel}</p>
                      <p className="font-medium text-green-600">{threeYrRet}%</p>
                    </div>
                  )}
                </div>
              </BFLLink>
            );
          })}
        </Carousel>
      </BFLAccordion>
    </div>
  );
};

export default OtherFunds;
